import { createContext, useContext, useEffect, useState, useMemo, useCallback } from "react";
import { STRINGS } from "../i18n/strings";

const I18nContext = createContext(null);

export function I18nProvider({ children }) {
  const [lang, setLangState] = useState(() => {
    if (typeof window === "undefined") return "en";
    const saved = localStorage.getItem("co_lang");
    if (saved === "fr" || saved === "en") return saved;
    // Auto-detect from browser
    const nav = (navigator.language || "en").toLowerCase();
    return nav.startsWith("fr") ? "fr" : "en";
  });

  const setLang = useCallback((next) => {
    setLangState(next);
    try { localStorage.setItem("co_lang", next); } catch {}
  }, []);

  useEffect(() => {
    if (typeof document !== "undefined") {
      document.documentElement.lang = lang;
    }
  }, [lang]);

  const t = useCallback((key) => {
    const entry = STRINGS[key];
    if (!entry) {
      // Fail loudly in dev but never crash UI
      // eslint-disable-next-line no-console
      console.warn(`[i18n] Missing translation key: ${key}`);
      return key;
    }
    return entry[lang] ?? entry.en ?? key;
  }, [lang]);

  // Pick correct field from a backend-supplied object {id, text_en, text_fr, fun_fact_en, fun_fact_fr,...}
  const pick = useCallback((obj, field) => {
    if (!obj) return "";
    return obj[`${field}_${lang}`] ?? obj[`${field}_en`] ?? obj[field] ?? "";
  }, [lang]);

  const value = useMemo(() => ({ lang, setLang, t, pick }), [lang, setLang, t, pick]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n() {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error("useI18n must be used inside <I18nProvider>");
  return ctx;
}
